import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Customer, CustomerContactInfo, FeatureFlagTypes, OriginEntityType, Permissions } from '@raptor-fe/core';
import { ApplicationContextStore, CustomerApiService, TranslationPipe, TranslationService } from '@raptor-fe/services';
import { CustomerTextWindowService } from './customer-text-window.service';
import { gateReason } from './gating';

/**
 * The self-contained drop-in that packages the whole feature (Spec #76394 Frontends part). Dropped
 * into any host with a `customerId`; a toolbar shows its own button (`showButton = true`), a list row
 * hosts it with `showButton = false` and calls `open()` from the row's existing control.
 *
 * All gating lives here: the button renders ONLY when the customer can actually be texted -- the
 * `customer:text` permission is held, the CustomerTexting feature flag is on, the customer has a
 * phone number, and the primary contact has approved texting. When any of those is missing there is
 * no messaging surface to offer, so the launcher renders nothing at all rather than a disabled
 * control. Hosts read `canMessage()` to gate their own message affordance the same way. `open()`
 * enforces the same gate so a host cannot bypass it.
 *
 * Ported from `omni-web-mockups/customer-messaging/customer-text-launcher.component.ts`. The template
 * and the disabled-with-reason / `open()` re-check logic are copied verbatim (Tier 1/2); the mockup's
 * demo-harness `MessagingDevFlagsService` and static `contacts.json` are replaced by the real
 * `ApplicationContextStore` permission + feature flag and the customer's resolved primary contact
 * (Tier 3), and the fixed strings are localized through `TranslationService`.
 *
 * Contact resolution: the customer's consent + phone (`CustomerContactInfo`) is accepted as an
 * optional `contact` input. The four F4 entry-point hosts (inspection / work-order / quote / customer
 * detail) already hold the loaded `Customer` in their own stores, so they pass the primary-contact
 * view directly -- no extra request, and the gate reflects exactly the record on screen. When the
 * input is absent the launcher stays a true drop-in by lazily resolving it from
 * `CustomerApiService.get`, so a bare `customerId` host still gates correctly.
 */
@Component({
    selector: 'lib-customer-text-launcher',
    imports: [TranslationPipe],
    template: `
        @if (showButton() && canMessage()) {
            <button type="button" class="btn btn-outline-primary" [title]="enabledTitle()" (click)="open()">
                <i class="bi bi-chat-dots me-1"></i>{{ { feature: 'Customer Messaging', component: 'Message Launcher', label: 'Message' } | translate }}
            </button>
        }
    `,
})
export class CustomerTextLauncherComponent {
    private readonly windowService = inject(CustomerTextWindowService);
    private readonly appContext = inject(ApplicationContextStore);
    private readonly customerApi = inject(CustomerApiService);
    private readonly translationService = inject(TranslationService);
    private readonly destroyRef = inject(DestroyRef);

    readonly customerId = input.required<string>();
    /** Read-only customer identity + consent state. Passed by hosts that already hold the customer; resolved lazily otherwise. */
    readonly contact = input<CustomerContactInfo | null>(null);
    readonly originEntityId = input<string | null>(null);
    readonly originEntityType = input<OriginEntityType | null>(null);
    readonly showButton = input(true);

    /** `customer:text` permission -- absent means the launcher renders nothing at all. */
    protected readonly permissionHeld = computed(() => this.appContext.hasPermission(Permissions.Customer.Text));

    /** CustomerTexting feature flag -- gates the whole feature per tenant. */
    private readonly featureEnabled = computed(() => this.appContext.features().includes(FeatureFlagTypes.CustomerTexting));

    /** Fallback contact resolved from the API when a host supplied only a `customerId`. */
    private readonly fallbackContact = signal<CustomerContactInfo | null>(null);

    /** The effective contact: the host-supplied input when present, else the lazily-resolved fallback. */
    private readonly resolvedContact = computed<CustomerContactInfo | null>(() => this.contact() ?? this.fallbackContact());

    protected readonly reason = computed(() => gateReason(this.resolvedContact() ?? undefined, this.featureEnabled()));
    protected readonly enabled = computed(() => this.reason() === 'ok');

    /**
     * Whether this customer can actually be texted from this surface: the permission is held and the
     * gate is fully clear. Hosts that render their own message affordance (a list-row menu item) read
     * this to hide it when messaging is not possible, so no dead control is ever shown.
     */
    readonly canMessage = computed(() => this.permissionHeld() && this.enabled());

    protected readonly enabledTitle = computed(() =>
        this.translationService.getTranslation('Customer Messaging', 'Message Launcher', 'Message customer'),
    );

    constructor() {
        effect(() => {
            const held = this.permissionHeld();
            const hostContact = this.contact();
            const id = this.customerId();
            if (!held || hostContact) {
                return;
            }
            untracked(() => this.resolveFallbackContact(id));
        });
    }

    /** Imperative open used by list-row hosts. Re-checks the gate -- consent cannot be bypassed. */
    open(): void {
        if (!this.permissionHeld() || this.reason() !== 'ok') {
            return;
        }
        this.windowService.open({
            customerId: this.customerId(),
            originEntityId: this.originEntityId(),
            originEntityType: this.originEntityType(),
        });
    }

    /** Lazily resolves the primary-contact consent/phone when no host input was supplied. */
    private resolveFallbackContact(customerId: string): void {
        this.customerApi
            .get(this.appContext.tenantId(), customerId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((customer) => this.fallbackContact.set(toContactInfo(customer)));
    }
}

/** Derives the read-only contact view from a loaded customer's primary contact. */
function toContactInfo(customer: Customer | null): CustomerContactInfo | null {
    if (!customer || !customer.id) {
        return null;
    }
    const primary = customer.primaryContact;
    return {
        customerId: customer.id,
        name: primary?.name ?? customer.name,
        phoneNumber: primary?.phoneNumber ?? null,
        isTextApproved: primary?.isTextApproved ?? false,
    };
}
